import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  {
    value: "light",
    label: "Light",
    desc: "Regular interior tidy. Daily-driver level of dust, a few crumbs.",
  },
  {
    value: "moderate",
    label: "Moderate",
    desc: "Some stains, dust on every surface, dirty floor mats. Most cars sit here.",
  },
  {
    value: "heavy",
    label: "Heavy",
    desc: "Pet hair, food spills, sticky cup-holders, road salt on the exterior.",
  },
  {
    value: "extreme",
    label: "Extreme",
    desc: "Mud, mildew, heavy stains, smoke smell. We can still bring it back.",
  },
];

export default function ConditionStep({ value, notes, onChange, onNext, onBack, loading }) {
  const [selected, setSelected] = useState(value || "");
  const [text, setText] = useState(notes || "");

  const submit = () => {
    if (!selected) {
      toast.error("Pick a condition that best describes your car");
      return;
    }
    onChange(selected, text);
    onNext(selected, text);
  };

  return (
    <div data-testid="step-condition">
      <p className="overline mb-6">Step 03 · Condition</p>
      <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[0.95] mb-4">
        How <span className="gold-gradient-text italic">dirty</span>
        <br />
        are we talking?
      </h2>
      <p className="text-white/55 mb-10">
        Be honest — the dirtier the car, the more time we spend with it. This helps us prepare.
      </p>

      <div className="space-y-3">
        {OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => {
              setSelected(o.value);
              onChange(o.value, text);
            }}
            className={`block w-full text-left p-6 border transition-all ${
              selected === o.value
                ? "border-gold bg-gold/5"
                : "border-white/10 hover:border-white/30 bg-ink-paper"
            }`}
            data-testid={`condition-${o.value}`}
          >
            <div className="flex items-baseline justify-between mb-2">
              <span
                className={`font-serif text-2xl ${
                  selected === o.value ? "text-gold" : "text-white"
                }`}
              >
                {o.label}
              </span>
              <span
                className={`text-[10px] uppercase tracking-[0.3em] ${
                  selected === o.value ? "text-gold" : "text-white/30"
                }`}
              >
                {selected === o.value ? "Selected" : "Tap to select"}
              </span>
            </div>
            <p className="text-sm text-white/55">{o.desc}</p>
          </button>
        ))}
      </div>

      <div className="mt-8">
        <label className="block mb-3 text-xs tracking-[0.25em] uppercase text-white/50">
          Anything else to know? (optional)
        </label>
        <textarea
          className="input-luxe"
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Pet stains in the back row, dog-friendly products, etc."
          data-testid="condition-notes-input"
        />
      </div>

      <div className="mt-12 flex justify-between">
        <button className="btn-secondary" onClick={onBack} disabled={loading} data-testid="condition-back-button">
          <ArrowLeft size={14} /> Back
        </button>
        <button className="btn-primary" onClick={submit} disabled={loading} data-testid="condition-next-button">
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Calculating…
            </>
          ) : (
            <>
              Continue <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
